import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { Path } from '../world/Path.js';
import { Player } from '../entities/Player.js';
import { collectCoins, collectPowerUp } from '../systems/Collision.js';
import { speedAt } from '../systems/Speed.js';

const CH = CONFIG.chaser;
const PU = CONFIG.powerUps;
const ST = CONFIG.stumble;
const HISTORY = 512; // muestras del recorrido del jugador (~128 m)
const HISTORY_STEP = 0.25; // metros entre muestras
const _target = new THREE.Vector3();

/**
 * Simulación de una partida, sin render ni interfaz.
 * La usan tanto el juego (Game) como las pruebas automáticas en Node (scripts/simulate.mjs).
 *
 * step() devuelve el evento del paso:
 *   'turn' | 'stumble' | 'smash' | 'powerUp' | 'fell' | 'fellSide' | 'pit' | 'crash' | 'caught' | null
 * Los eventos que terminan la partida están en World.FATAL.
 */
export class World {
  static FATAL = new Set(['fell', 'fellSide', 'pit', 'crash', 'caught']);

  constructor(scene) {
    this.path = new Path(scene, speedAt);
    this.player = new Player(scene);
    this.playerWorld = new THREE.Vector3(); // posición del jugador en el mundo (también sin render)
    this.history = new Float32Array(HISTORY * 3); // [distancia, x, z] × HISTORY, circular
    this.flyingCoins = [];
    this.autoTurnRand = Math.random;
    this.god = false; // solo para pruebas (?god=1): invencible y gira solo en las curvas
    this.reset(1);
  }

  reset(seed, { startGap = CH.farGap } = {}) {
    this.seed = seed;
    this.path.reset(seed);
    this.player.reset(this.path);
    this.coins = 0;
    this.points = 0;
    this.streakStart = 0; // distancia del último tropiezo
    this.lastPowerUp = null;
    this.effects = { magnet: 0, shield: 0, sprint: 0 };
    this.grace = 0; // invencibilidad tras acabar el sprint
    this.slow = 1; // factor de velocidad tras un tropiezo
    this.chaser = { gap: startGap, near: 0 }; // near > 0: un tropiezo más y te atrapa
    this.flyingCoins.length = 0;

    // Historial inicial: una recta por detrás de la salida, para que el perseguidor tenga por dónde ir.
    this.historyHead = 0;
    this.historyCount = 0;
    const first = this.path.first;
    for (let d = -HISTORY_STEP * (HISTORY - 1); d <= 0; d += HISTORY_STEP) {
      this.path.worldPoint(first, d, 0, _target);
      this.#pushHistory(d, _target.x, _target.z);
    }
    this.lastHistory = 0;
    this.path.worldPoint(first, 0, 0, this.playerWorld);
  }

  /** Puntos: 1 por metro y 10 por moneda, multiplicados por la racha sin tropiezos. */
  get score() {
    return Math.floor(this.points);
  }

  /** x1…x5: sube cada CONFIG.score.multiplierEvery metros sin tropezar. */
  get multiplier() {
    const S = CONFIG.score;
    return Math.min(S.maxMultiplier, 1 + Math.floor((this.player.distance - this.streakStart) / S.multiplierEvery));
  }

  get invulnerable() {
    return this.god || this.effects.shield > 0 || this.effects.sprint > 0 || this.grace > 0;
  }

  /**
   * Segunda oportunidad tras morir: reaparece al principio del tramo siguiente (en un cruce en T sin
   * decidir, en la rama izquierda) con unos segundos de invencibilidad y el monstruo lejos.
   */
  revive() {
    const p = this.player;
    const seg = p.segment;
    if (!seg.next && seg.branches) this.path.choose(seg, seg.branches[0]);
    if (!seg.next) this.path.fill(seg, p.s);
    const next = seg.next;
    p.respawn(next);
    for (const k in this.effects) this.effects[k] = 0;
    this.grace = CONFIG.revive.grace;
    this.slow = 1;
    this.chaser.gap = CH.farGap;
    this.chaser.near = 0;
    this.flyingCoins.length = 0;
    this.path.fill(next, p.s);
    this.path.recycle(next, p.s);
    this.path.worldPoint(next, p.s, 0, this.playerWorld);
    this.#pushHistory(p.distance, this.playerWorld.x, this.playerWorld.z);
    this.lastHistory = p.distance;
  }

  /** Activa (o renueva) un potenciador. */
  activate(type) {
    this.effects[type] = PU[type];
    if (type === 'sprint') this.grace = 0;
  }

  /** Avanza dt segundos. */
  step(dt) {
    const p = this.player;
    this.#tickEffects(dt);

    if (p.state === 'running') {
      this.slow = Math.min(1, this.slow + ((1 - ST.slowFactor) / ST.recovery) * dt);
      const sprint = this.effects.sprint > 0 ? PU.sprintMultiplier : 1;
      p.speed = speedAt(p.distance) * sprint * this.slow;
      p.invulnerable = this.invulnerable;
      p.pitProof = this.god || this.effects.sprint > 0 || this.grace > 0; // el escudo no salva de los fosos
      if (this.god || this.effects.sprint > 0) this.#autoTurn(p);
    }

    const before = p.distance;
    let event = p.update(dt);
    this.points += (p.distance - before) * this.multiplier;

    if (event === 'stumble') event = this.#stumble(p);
    else if (event === 'smash') this.path.breakObstacle(p.lastHit);
    else if (event === 'crash') this.chaser.near = CH.memory; // se acerca a ver la escena

    if (p.state === 'running') {
      this.path.worldPoint(p.segment, p.s, p.x, this.playerWorld);
      this.playerWorld.y = p.y;
      const got = collectCoins(p, this.path);
      this.coins += got;
      this.points += got * CONFIG.score.coinValue * this.multiplier;
      this.#updateMagnet(dt);
      const pu = collectPowerUp(p, this.path);
      if (pu) {
        this.activate(pu);
        this.lastPowerUp = pu;
        event ??= 'powerUp';
      }
      this.path.fill(p.segment, p.s);
      this.path.recycle(p.segment, p.s);
      if (p.distance - this.lastHistory >= HISTORY_STEP) {
        this.#pushHistory(p.distance, this.playerWorld.x, this.playerWorld.z);
        this.lastHistory = p.distance;
      }
    }
    this.#updateChaser(dt);
    return event;
  }

  /**
   * Posición del perseguidor: el punto del recorrido del jugador `gap` metros por detrás.
   * Escribe en out (x, z) y devuelve el rumbo (yaw) en el que avanza.
   */
  chaserPose(out) {
    const d = this.player.distance - this.chaser.gap;
    this.#sampleHistory(d, out);
    this.#sampleHistory(d - 0.6, _target);
    return Math.atan2(-(out.x - _target.x), -(out.z - _target.z));
  }

  // ---------- Reglas ----------

  #stumble(p) {
    this.streakStart = p.distance; // se pierde la racha
    const o = p.lastHit;
    if (o.type === 'low' && o.hit) this.path.breakObstacle(o); // el tronco sale despedido
    this.slow = ST.slowFactor;
    if (this.chaser.near > 0) {
      // Segundo tropiezo seguido: el monstruo te alcanza.
      p.catchMe();
      return 'caught';
    }
    this.chaser.near = CH.memory;
    return 'stumble';
  }

  #tickEffects(dt) {
    const e = this.effects;
    const sprinting = e.sprint > 0;
    for (const k in e) e[k] = Math.max(0, e[k] - dt);
    if (sprinting && e.sprint === 0) this.grace = PU.sprintGrace;
    else this.grace = Math.max(0, this.grace - dt);
  }

  /** Durante el sprint, el jugador gira solo en las curvas (en los cruces en T, al azar). */
  #autoTurn(p) {
    const seg = p.segment;
    if (!(seg.turn || seg.tee) || p.queuedTurn || p.toCorner > p.speed * 0.2) return;
    p.requestLateral(seg.tee ? (this.autoTurnRand() < 0.5 ? -1 : 1) : seg.turn);
  }

  /** Imán: las monedas cercanas (también tras la próxima curva) vuelan hacia el jugador. */
  #updateMagnet(dt) {
    const p = this.player;
    if (this.effects.magnet > 0) {
      const r2 = PU.magnetRadius ** 2;
      for (const seg of [p.segment, p.segment.next]) {
        if (!seg) continue;
        for (const c of seg.coins) {
          if (c.taken || c.flying || c.pos.distanceToSquared(this.playerWorld) > r2) continue;
          c.flying = true;
          this.flyingCoins.push(c);
        }
      }
    }
    _target.copy(this.playerWorld);
    _target.y += 1;
    const step = (p.speed + 20) * dt;
    for (let i = this.flyingCoins.length - 1; i >= 0; i--) {
      const c = this.flyingCoins[i];
      if (!c.taken) {
        const dist = c.pos.distanceTo(_target);
        if (dist > 0.6) {
          c.pos.lerp(_target, Math.min(1, step / dist));
          continue;
        }
        this.path.takeCoin(c);
        this.coins += c.value;
        this.points += c.value * CONFIG.score.coinValue * this.multiplier;
      }
      this.flyingCoins.splice(i, 1);
    }
  }

  #updateChaser(dt) {
    const c = this.chaser;
    const state = this.player.state;
    c.near = Math.max(0, c.near - dt);
    let target = c.near > 0 ? CH.closeGap : CH.farGap;
    if (state === 'caught') target = CH.caughtGap;
    else if (state === 'crashed') target = CH.closeGap * 0.6;
    else if (state === 'falling') return; // se detiene al borde
    const rate = target < c.gap ? CH.approachSpeed : CH.recedeSpeed;
    c.gap += Math.max(-rate * dt, Math.min(rate * dt, target - c.gap));
  }

  // ---------- Historial del recorrido ----------

  #pushHistory(d, x, z) {
    const i = this.historyHead * 3;
    this.history[i] = d;
    this.history[i + 1] = x;
    this.history[i + 2] = z;
    this.historyHead = (this.historyHead + 1) % HISTORY;
    this.historyCount = Math.min(this.historyCount + 1, HISTORY);
  }

  #sampleHistory(d, out) {
    const h = this.history;
    let newer = -1;
    for (let k = 0; k < this.historyCount; k++) {
      const i = ((this.historyHead - 1 - k + HISTORY) % HISTORY) * 3;
      if (h[i] <= d) {
        if (newer < 0) return out.set(h[i + 1], 0, h[i + 2]);
        const t = (d - h[i]) / (h[newer] - h[i] || 1);
        return out.set(h[i + 1] + (h[newer + 1] - h[i + 1]) * t, 0, h[i + 2] + (h[newer + 2] - h[i + 2]) * t);
      }
      newer = i;
    }
    return newer < 0 ? out.set(0, 0, 0) : out.set(h[newer + 1], 0, h[newer + 2]);
  }
}
